import React, { Component, Fragment } from 'react';
import Input from '../shared/input';
import debounce from 'lodash/debounce';
import HttpRequestor from '~/components/utils/http_requestor';
import Tick from '../../images/tick-green.svg';
import I18n from '~/utils/translations';
import UserTypeInput from '~/components/book_viewing/user_type_input';
import { validEmail } from '~/utils/validators';
import scrollToElement from 'scroll-to-element';
import SubmitError from '~/components/book_viewing/submit_error';
import CustomPreQualificationQuestions from './custom_pre_qualification_questions';

export default class LoginOrSignupForm extends Component {
  constructor(props) {
    super(props);

    let defaultEmail = this.props.defaultEmail;
    if (!defaultEmail && sessionStorage.getItem('foundEmail')) {
      defaultEmail = sessionStorage.getItem('foundEmail');
    }

    this.emailInput = null;

    this.state = {
      foundUser: null,
      email: defaultEmail || '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      searching: false,
      resubmitting: false,
      resubmitted: false,
      loading: false,
      user_type: null,
      triggerPreQualValidation: false,
    };

    this.delayedLookup = debounce(this.lookupUser, 500);
  }

  componentDidMount() {
    if (this.state.email != '' && !this.state.registering) {
      this._lookup();
    } else {
      this.emailInput && this.emailInput.focus();
    }
  }

  get lookupUserUrl() {
    return `/users/lookup?email=${encodeURIComponent(this.state.email)}&property_id=${this.props.property.id}`;
  }

  onEmailChange = (e) => {
    e.persist();
    this.setState({ email: e.target.value });
    this.delayedLookup(e);
  };

  onInformationChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  onUserTypeChange = (option) => {
    this.setState({ user_type: option });
  };

  onInputChange = (key, value) => {
    this.setState({ [key]: value });
  };

  onInputFocus = (e) => {
    const trackLabel = !!e.target.value ? 'present' : 'empty';
    track_event(this.props.trackCategory, 'field-focus', `${e.target.name}-${trackLabel}`);
  };

  onInputBlur = (e) => {
    const trackLabel = !!e.target.value ? 'present' : 'empty';
    track_event(this.props.trackCategory, 'field-blur', `${e.target.name}-${trackLabel}`);
  };

  lookupUser(e) {
    if (this.state.email === '' || !validEmail(e.target.value)) {
      return this.setState(
        {
          foundUser: null,
        },
        () => {
          if (this.props.onUserChanged) {
            this.props.onUserChanged({});
          }
        }
      );
    }

    if (e.target.validity.valid) {
      this._lookup();
    }
  }

  _lookup = () => {
    this.setState({ searching: true });
    const requestor = new HttpRequestor();
    requestor.call(this.lookupUserUrl, 'GET').then(this._lookupSuccess).catch(this._lookupError);
  };

  _lookupSuccess = (response) => {
    this.setState(
      {
        searching: false,
        foundUser: response,
      },
      () => {
        if (this.props.onUserChanged) {
          this.props.onUserChanged(response);
        }
      }
    );
  };

  _lookupError = (response) => {
    this.setState(
      {
        searching: false,
        foundUser: {},
        searchError: 'Something went wrong, please try again',
      },
      () => {
        if (!this.props.onUserChanged) {
          this.props.onUserChanged({});
        }
      }
    );
  };

  onValidationComplete = () => {
    this.setState({ triggerPreQualValidation: false });
  };

  preQualAnswersIncomplete = () => {
    const answers = this.buildCustomPreQualAnswers();
    const errors = {};
    return this.props.preQualificationQuestions.some((requiredQuestion) => {
      if (requiredQuestion.required) {
        const answer = answers[requiredQuestion.key];
        if (answer == null) {
          return true;
        }
      }
      return false;
    });
  };

  registerUser = (e) => {
    if (this.props.property.preQualificationEnabled) {
      const preQualIncomplete = this.preQualAnswersIncomplete();
      if (preQualIncomplete) {
        this.setState({ triggerPreQualValidation: true });
        setTimeout(() => scrollToElement('.validation-error', { offset: -100, duration: 250 }, 500));

        return;
      } else {
        this.setState({ triggerPreQualValidation: false });
      }
    }
    const params = this.registerParams();
    this._register(params);
    return;
  };

  registerParams = () => {
    const defaultParams = {
      email: this.state.email,
      first_name: this.state.firstName,
      last_name: this.state.lastName,
      phone: this.state.phoneNumber,
      property_id: this.props.property.id,
      listing_id: this.props.listing,
    };

    this.addPreQualificationParams(defaultParams);
    this.addBrokersParams(defaultParams);

    return Object.assign({}, defaultParams, this.props.extraRegisterParams);
  };

  addPreQualificationParams = (params) => {
    if (this.props.property.preQualificationEnabled) {
      params.custom_prequalification_answers = this.buildCustomPreQualAnswers();
    }
  };

  buildCustomPreQualAnswers = () => {
    const preQualAnswers = {};

    this.props.preQualificationQuestions.forEach((question) => {
      const key = question.key;
      const value = this.state[key];
      preQualAnswers[key] = value;
    });

    return preQualAnswers;
  };

  addBrokersParams = (params) => {
    if (this.props.property.brokers_enabled) {
      params.user_type = this.state.user_type.value;
    }
  };

  _register = (params) => {
    this.setState({ registering: true });
    if (this.trackingPropsPresent) {
      track_event(
        this.props.trackCategory,
        this.props.trackAction,
        this.props.trackLabel,
        null,
        this.props.facebookEvent
      );
    }

    const requestor = new HttpRequestor();
    requestor
      .call(this.props.registerUrl, 'POST', {
        data: {
          type: 'user',
          attributes: { params },
        },
      })
      .then(this._registerSuccess)
      .catch(this._registerError);
  };

  _registerSuccess = (response) => {
    if (response && response.property_interest_id) {
      track_viewing(response.property_interest_id);
    }

    this.setState({ registered: true });
  };

  _registerError = (response) => {
    this.setState(
      {
        registering: false,
        registerError: response.error ? response.error : 'Something went wrong, please try again',
      },
      1000
    );
  };

  get trackingPropsPresent() {
    return !!this.props.trackCategory;
  }

  validEmail = () => {
    return this.state.email != '' && this.state.foundUser != null;
  };

  validInformation() {
    const baseValidation =
      this.state.firstName !== '' &&
      this.state.lastName !== '' &&
      this.state.phoneNumber !== '' &&
      (!this.props.property.brokers_enabled || this.state.user_type != null);

    return baseValidation;
  }

  linkableEmail() {
    return encodeURIComponent(this.state.email);
  }

  handleSignIn = (e) => {
    e.preventDefault();
    if (this.props.onSignIn) {
      this.props.onSignIn();
    }
    window.location = e.target.href;
  };

  renderKnownUser() {
    const loading = this.state.registering || this.props.loading;

    return (
      <Fragment>
        {!this.props.signedIn && (
          <Fragment>
            <p className="about-you__context">
              <span className="about-you__emphasis">{I18n.t('login_or_signup.known_user.title')}</span>{' '}
              {I18n.t('login_or_signup.known_user.text')}
            </p>
            <a
              href={`${this.props.doorbellHost}?email=${this.linkableEmail()}&redirect_to=${this.props.signInRedirect}`}
              onClick={this.handleSignIn}
              className="about-you__button"
            >
              {I18n.t('login_or_signup.known_user.button')}
            </a>
          </Fragment>
        )}

        {this.props.signedIn && (
          <Fragment>
            {this.props.property.preQualificationEnabled && this.renderPreQualificationQuestions()}

            <button
              className={`about-you__button ${loading ? 'loading' : ''}`}
              disabled={loading}
              onClick={this.registerUser}
            >
              {this.props.buttonText}
            </button>
            {!this.state.registering && this.state.registerError && (
              <SubmitError errorMessage={this.state.registerError} />
            )}
          </Fragment>
        )}
      </Fragment>
    );
  }

  renderNewUser() {
    const loading = this.state.registering || this.props.loading;

    return (
      <Fragment>
        <p className="about-you__context">
          <span className="about-you__emphasis">{I18n.t('login_or_signup.new_user.title')}</span>
          <br />
          {I18n.t('login_or_signup.new_user.text')}
        </p>
        <div className="about-you__inner about-you__new-user-information">
          <div className="about-you__name-wrapper">
            <Input
              label={I18n.t('login_or_signup.labels.first_name')}
              name="firstName"
              className="about-you__first-name"
              onChange={this.onInformationChange}
              onFocus={this.onInputFocus}
              onBlur={this.onInputBlur}
            />
            <Input
              label={I18n.t('login_or_signup.labels.last_name')}
              name="lastName"
              className="about-you__last-name"
              onChange={this.onInformationChange}
              onFocus={this.onInputFocus}
              onBlur={this.onInputBlur}
            />
          </div>
          <Input
            label={I18n.t('login_or_signup.labels.phone')}
            name="phoneNumber"
            className="about-you__phone"
            onChange={this.onInformationChange}
            onFocus={this.onInputFocus}
            onBlur={this.onInputBlur}
          />

          {this.props.property.brokers_enabled && this.renderUserTypeInput()}

          {this.props.property.preQualificationEnabled && this.renderPreQualificationQuestions()}
        </div>

        <button
          className={`about-you__button ${loading ? 'loading' : ''}`}
          disabled={!this.validInformation() || loading}
          onClick={this.registerUser}
        >
          {this.props.buttonText}
        </button>
        {!this.state.registering && this.state.registerError && <SubmitError errorMessage={this.state.registerError} />}
      </Fragment>
    );
  }

  renderUserTypeInput = () => {
    return <UserTypeInput value={this.state.user_type} onChange={this.onUserTypeChange} />;
  };

  get signInUrl() {
    let url = `${this.props.doorbellHost}?redirect_to=${this.props.signInRedirect}`;
    if (this.state.email) {
      url += `&email=${this.linkableEmail()}`;
    }
    return url;
  }

  renderNextStep() {
    const { foundUser } = this.state;
    const loading = this.state.registering || this.props.loading;
    if (foundUser == null) {
      return (
        <p className="about-you__context">
          {I18n.t('login_or_signup.sign_in.title')}&nbsp;
          <a href={this.signInUrl} onClick={this.handleSignIn} className="about-you__sign-in-link">
            {I18n.t('login_or_signup.sign_in.link')} &rsaquo;
          </a>
        </p>
      );
    }

    // Sign in
    if (foundUser.user_exists && foundUser.user_registered) {
      return this.renderKnownUser();
    }

    if (foundUser.user_exists && !foundUser.user_registered) {
      if (this.props.renderLastSectionButtons) {
        return this.props.renderLastSectionButtons;
      }
      return (
        <Fragment>
          {this.props.property.preQualificationEnabled && this.renderPreQualificationQuestions()}

          <button
            className={`about-you__button ${loading ? 'loading' : ''}`}
            disabled={loading}
            onClick={this.registerUser}
          >
            {this.props.buttonText}
          </button>
        </Fragment>
      );
    }

    // Register
    return this.renderNewUser();
  }

  renderEmailValid = () => {
    if (this.state.searching) {
      return (
        <div className="about-you__email-validity">
          <div className="about-you__email-validity-indicator--loading"></div>
        </div>
      );
    }

    if (this.state.foundUser !== null) {
      return (
        <div className="about-you__email-validity">
          <img className="about-you__email-validity-indicator--found" src={Tick} />
        </div>
      );
    }

    return null;
  };

  renderPreQualificationQuestions() {
    const newProps = {
      questions: this.props.preQualificationQuestions,
      answers: this.props.customPreQualificationAnswers,
      moveInAvailableFrom: this.props.property.moveInAvailableFrom,
      onInputChange: (key, value) => this.onInputChange(key, value),
      onValidationComplete: this.onValidationComplete,
      triggerPreQualValidation: this.state.triggerPreQualValidation,
    };

    return (
      <div className="about-you__inner about-you__new-user-information">
        <CustomPreQualificationQuestions {...newProps} />
      </div>
    );
  }

  render() {
    return (
      <section className="about-you">
        <h3 className="about-you__title">About you</h3>
        <div className="about-you__inner">
          <Input
            inputRef={(elm) => (this.emailInput = elm)}
            label={I18n.t('login_or_signup.labels.email')}
            type="email"
            name="email"
            className="about-you__email"
            onChange={this.onEmailChange}
            onFocus={this.onInputFocus}
            onBlur={this.onInputBlur}
            inputAddon={this.renderEmailValid()}
            value={this.state.email}
          />
        </div>
        {this.renderNextStep()}
      </section>
    );
  }
}
